/**
 * One replaceable file of a pe3d game (a schema asset slot), drawn like an
 * asset row of the snapshot editor's Assets tab (snapshotEditor/containerAssets.js):
 * a raised card with the slot's name and state chips, a thumbnail, a dropdown
 * of what the slot can take and an Upload button.
 *
 * A slot uses the game's own file until the variant replaces it with a file
 * the release ships (another game variant's) or a media library file. The
 * library is shown one bucket at a time, each slot its own bucket (the bucket
 * label above the source): the dropdown lists the company's files in that
 * bucket, and an upload is filed under it. Whatever is chosen is read and
 * checked by the release on the server (size, triangles, clips, animations,
 * characters) and the card shows what it found, so a file that would break
 * the export is visible before anyone exports.
 */

import React, { Suspense, useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@material-ui/core";
import PublishIcon from "@material-ui/icons/Publish";
import SettingsBackupRestoreIcon from "@material-ui/icons/SettingsBackupRestore";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import StopIcon from "@material-ui/icons/Stop";
import ThreeDRotationIcon from "@material-ui/icons/ThreeDRotation";
import CodeIcon from "@material-ui/icons/Code";
import { FIELD_LABEL, PANEL_BG, SECTION_DIVIDER } from "../snapshotEditor/panelPalette";
import { CARD_STYLE, ERROR_TEXT, MUTED, OUTLINED_BUTTON_STYLE } from "./pe3dPanelKit";

// A file of a multi-file upload that has been picked but not uploaded yet — the snapshot editor's
// green tick (snapshotEditor/containerAssets.js), so the two upload flows read alike.
const DialogGlbOptimizer = React.lazy(() => import("../accountSettingsComponents/dialogGlbOptimizer"));

const STAGED_BUTTON_STYLE = { color: "#4caf50", borderColor: "#4caf50" };
// The "+ IMG / + JSON" buttons: as small as their label, never wrapping it.
const PART_BUTTON_STYLE = { ...OUTLINED_BUTTON_STYLE, minWidth: 0, padding: "1px 8px", fontSize: 12, whiteSpace: "nowrap" };
import BucketPicker from "../playEngineBucketPicker";
import { BUNDLE_REF, LIBRARY, UPLOAD_PARTS, isBundleRef, isLibraryRef, uploadToLibrary } from "../../modules/pe3dLibrary";
import { NO_BUCKET } from "../../modules/playEngineAssetUtils";
import DialogPe3dModelInspect from "./dialogPe3dModelInspect";
import DialogPe3dDataInspect from "./dialogPe3dDataInspect";


const DEFAULT = "default";
const CURRENT = "current";

/** "1.2 MB · 4 812 tris · clips: run, idle" from the checker's meta. */
export function describeMeta(meta) {
  if (!meta) return "";
  const parts = [];
  if (meta.bytes) parts.push(formatBytes(meta.bytes));
  if (meta.triangles !== undefined) parts.push(`${meta.triangles} tris`);
  if (meta.width && meta.height) parts.push(`${meta.width}×${meta.height}`);
  if (meta.clips && typeof meta.clips === "object") {
    const names = Object.keys(meta.clips);
    if (names.length) parts.push(`clips: ${names.slice(0, 6).join(", ")}${names.length > 6 ? "…" : ""}`);
  }
  if (meta.animations && typeof meta.animations === "object") {
    const names = Object.keys(meta.animations);
    if (names.length) parts.push(`animations: ${names.slice(0, 6).join(", ")}${names.length > 6 ? "…" : ""}`);
  }
  if (meta.duration) parts.push(`${Number(meta.duration).toFixed(1)} s`);
  return parts.join(" · ");
}

function formatBytes(bytes) {
  return bytes >= 1e6 ? `${(bytes / 1e6).toFixed(2)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function limitsOf(def) {
  return [
    def.maxKB && `≤ ${def.maxKB} KB`,
    def.maxTris && `≤ ${def.maxTris} tris`,
    def.maxSize && `≤ ${def.maxSize} px`,
    def.clips && `needs clips: ${def.clips.join(", ")}`,
    def.animations && `needs animations: ${def.animations.join(", ")}`,
    def.chars && `needs chars: ${def.chars}`,
  ]
    .filter(Boolean)
    .join(" · ");
}

const fileName = (path) => String(path || "").split("/").pop();

function Choice({ thumb, label }) {
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
      {thumb && (
        <img
          src={thumb}
          alt=""
          style={{ height: 30, maxWidth: 60, objectFit: "contain", flexShrink: 0 }}
          onError={(event) => {
            event.target.style.display = "none";
          }}
        />
      )}
      <Typography variant="body2" noWrap style={{ minWidth: 0 }}>
        {label}
      </Typography>
    </span>
  );
}

const TILE_STYLE = {
  width: 44,
  height: 44,
  flexShrink: 0,
  boxSizing: "border-box",
  borderRadius: 3,
  backgroundColor: PANEL_BG,
  border: `1px solid ${SECTION_DIVIDER}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  padding: 0,
};

// What the tile does for a file it cannot picture.
const TILE_ACTIONS = {
  audio: { title: "Play this sound", icon: PlayArrowIcon },
  model: { title: "Inspect the model in 3D", icon: ThreeDRotationIcon },
  data: { title: "Inspect the JSON", icon: CodeIcon },
};

/**
 * The 44px square left of the source: the picture of an image, else the
 * slot's own action — play a sound, inspect a model or a JSON file.
 */
function SourceTile({ thumb, kind, isPlaying, onPlay, onInspect }) {
  if (thumb) {
    return (
      <div style={TILE_STYLE}>
        <img src={thumb} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
      </div>
    );
  }
  const action = TILE_ACTIONS[kind];
  if (!action) {
    return (
      <div style={TILE_STYLE}>
        <Typography variant="caption" style={{ color: MUTED, fontSize: 10 }}>
          {kind}
        </Typography>
      </div>
    );
  }
  const playing = kind === "audio" && isPlaying;
  const Icon = playing ? StopIcon : action.icon;
  const color = playing ? "#ef5350" : kind === "audio" ? "#90caf9" : FIELD_LABEL;
  return (
    <Tooltip title={playing ? "Stop" : action.title}>
      <IconButton
        aria-label={playing ? "Stop" : action.title}
        onClick={kind === "audio" ? onPlay : onInspect}
        style={{ ...TILE_STYLE, color }}
        className="pe3d-source-tile"
      >
        <Icon />
      </IconButton>
    </Tooltip>
  );
}

/**
 * @param props.libraryItems  the company's files of this slot's kind in `bucket`
 *                            (pe3dLibrary listLibrary), null while loading
 * @param props.bucket        this slot's bucket; uploads go there
 * @param props.buckets       every bucket name to offer
 * @param props.bucketCounts  bucket → files of this kind in it
 * @param props.onBucketChange
 * @param props.onUploaded    after an upload, so the list is read again
 * @param props.mark          shown after the title: the language mark when the edited language sets this slot
 */
export default function Pe3dSlotCard({
  slot,
  def,
  value,
  release,
  releaseChoices,
  libraryItems,
  bucket = NO_BUCKET,
  buckets,
  bucketCounts,
  canEdit,
  onChange,
  onBucketChange,
  onUploaded,
  onReadOnly,
  createSnackbar,
  mark = null,
}) {
  const library = LIBRARY[def.kind];
  const [info, setInfo] = useState(null); // { file, meta, errors } of the chosen file
  const [isChecking, setIsChecking] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [optimizerOpen, setOptimizerOpen] = useState(false);
  // Multi-file kinds (atlas, bitmap font, Spine): each file picked on its own button first
  // (variant → File), uploaded together once all are there — the snapshot editor's "+ IMG / + JSON".
  const [staged, setStaged] = useState({});
  const parts = UPLOAD_PARTS[def.kind] || null;
  const partsReady = Boolean(parts && parts.every((part) => staged[part.variant]));
  const [thumb, setThumb] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [inspecting, setInspecting] = useState(null); // "model" | "data" | null
  const audioRef = useRef(null);
  const fileInput = useRef(null);
  const partInputs = useRef({});
  const valueKey = JSON.stringify(value);
  const own = release.ownFileOf(slot);

  useEffect(() => {
    let cancelled = false;
    setInfo(null);
    if (!value) return undefined;
    setIsChecking(true);
    release
      .describeSlot(slot, value)
      .then((described) => !cancelled && setInfo(described))
      .catch((err) => !cancelled && setInfo({ errors: [err.message] }))
      .finally(() => !cancelled && setIsChecking(false));
    return () => {
      cancelled = true;
    };
  }, [release, slot, valueKey]);

  useEffect(() => {
    let cancelled = false;
    release
      .slotThumbnail(slot, value)
      .then((url) => !cancelled && setThumb(url))
      .catch(() => !cancelled && setThumb(null));
    return () => {
      cancelled = true;
    };
  }, [release, slot, valueKey]);

  // A sound plays until it ends, is stopped, or the slot's file changes.
  const stopSound = () => {
    if (audioRef.current) audioRef.current.pause();
    audioRef.current = null;
    setIsPlaying(false);
  };
  useEffect(() => stopSound, [release, slot, valueKey]);
  const toggleSound = async () => {
    if (isPlaying) return stopSound();
    setIsPlaying(true);
    try {
      const source = await release.slotFileUrl(slot, value);
      if (!source) throw new Error("this slot's file could not be read");
      const audio = new Audio(source.url);
      audio.onended = () => {
        if (audioRef.current === audio) stopSound();
      };
      audioRef.current = audio;
      await audio.play();
    } catch (err) {
      stopSound();
      createSnackbar(`Could not play the sound — ${err.message}`, "error", false);
    }
    return undefined;
  };

  const upload = async (fileList, propagateError = false) => {
    if (!canEdit) {
      if (propagateError) throw new Error("You are not authorized to edit this model.");
      return onReadOnly && onReadOnly();
    }
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setIsUploading(true);
    try {
      onChange(await uploadToLibrary(def.kind, files, bucket));
      if (parts) setStaged({});
      if (onUploaded) onUploaded();
    } catch (err) {
      if (propagateError) throw err;
      createSnackbar(err.message, "error", false);
    } finally {
      setIsUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  // Every choice under a string key, which is what the Select works in.
  const items = libraryItems || [];
  const libraryIndex = items.findIndex((item) => JSON.stringify(item.ref) === valueKey);
  let selected = DEFAULT;
  if (isBundleRef(value)) selected = value;
  else if (isLibraryRef(value)) selected = libraryIndex >= 0 ? `library:${libraryIndex}` : CURRENT;
  const chosenName = info && info.file ? fileName(info.file) : "…";
  const ownName = fileName((own && own.file) || def.default);

  const choose = (key) => {
    if (!canEdit) return onReadOnly && onReadOnly();
    if (key === DEFAULT) return onChange(null);
    if (key.startsWith(BUNDLE_REF)) return onChange(key);
    if (key.startsWith("library:")) return onChange(items[Number(key.slice("library:".length))].ref);
    return undefined;
  };

  const renderValue = (key) => {
    if (key === DEFAULT) return <Choice thumb={thumb} label={`${ownName} (the game's)`} />;
    if (key === CURRENT) return <Choice thumb={thumb} label={`${chosenName} (another bucket)`} />;
    if (key.startsWith(BUNDLE_REF)) return <Choice thumb={thumb} label={`${fileName(key)} (shipped)`} />;
    const item = items[Number(key.slice("library:".length))];
    return <Choice thumb={item && item.thumb} label={item ? item.label : key} />;
  };

  let state = { label: "game's file", color: MUTED };
  if (isBundleRef(value)) state = { label: "shipped file", color: "#cfcf8a" };
  else if (isLibraryRef(value)) state = { label: "library", color: "#cfcf8a" };
  const size = value ? info && info.meta && info.meta.bytes : own && own.bytes;
  const limits = limitsOf(def);
  const errors = (info && info.errors) || [];

  return (
    <Card style={{ marginBottom: 8, ...CARD_STYLE }}>
      <CardContent style={{ paddingBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
          <Typography style={{ color: "#eee", fontWeight: 600, fontSize: 14 }}>
            {def.label || slot}
            {mark}
          </Typography>
          <Chip label={state.label} size="small" style={{ backgroundColor: PANEL_BG, color: state.color }} />
          {size ? (
            <Typography variant="caption" style={{ color: MUTED }}>
              {formatBytes(size)}
            </Typography>
          ) : null}
          <div style={{ flex: 1 }} />
          {(isChecking || isUploading) && <CircularProgress size={18} />}
          {value && (
            <Tooltip title="Use the game's own file">
              <span>
                <IconButton size="small" disabled={isUploading} onClick={() => choose(DEFAULT)}>
                  <SettingsBackupRestoreIcon fontSize="small" style={{ color: FIELD_LABEL }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </div>
        {limits && (
          <Typography variant="caption" style={{ color: MUTED, display: "block", marginBottom: 6 }}>
            {limits}
          </Typography>
        )}

        <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
          <Typography variant="caption" style={{ color: FIELD_LABEL, flex: 1 }}>
            Source
          </Typography>
          {library && (
            <BucketPicker
              bucket={bucket}
              buckets={buckets}
              bucketCounts={bucketCounts}
              disabled={!canEdit}
              onBucketChange={onBucketChange}
              style={{ padding: "2px 8px", fontSize: 11 }}
            />
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <SourceTile
            thumb={thumb}
            kind={def.kind}
            isPlaying={isPlaying}
            onPlay={toggleSound}
            onInspect={() => setInspecting(def.kind)}
          />
          <Select
            value={selected}
            disabled={isUploading}
            renderValue={renderValue}
            onChange={(event) => choose(event.target.value)}
            style={{ flex: 1, minWidth: 0 }}
            MenuProps={{ PaperProps: { style: { maxHeight: 420 } } }}
          >
            <MenuItem value={DEFAULT}>
              <Choice label={`${ownName} (the game's)`} />
            </MenuItem>
            {releaseChoices.length > 0 && (
              <MenuItem disabled style={{ fontSize: 11, textTransform: "uppercase", opacity: 1, color: MUTED }}>
                Shipped with the game
              </MenuItem>
            )}
            {releaseChoices.map((file) => (
              <MenuItem key={file} value={`${BUNDLE_REF}${file}`}>
                <Choice label={file} />
              </MenuItem>
            ))}
            {library && (
              <MenuItem disabled style={{ fontSize: 11, textTransform: "uppercase", opacity: 1, color: MUTED }}>
                Library · {bucket === NO_BUCKET ? "no bucket" : bucket}
              </MenuItem>
            )}
            {selected === CURRENT && (
              <MenuItem value={CURRENT}>
                <Choice label={`${chosenName} (another bucket)`} />
              </MenuItem>
            )}
            {library && libraryItems === null && <MenuItem disabled>Loading…</MenuItem>}
            {library && libraryItems && items.length === 0 && <MenuItem disabled>Nothing in this bucket yet — upload one</MenuItem>}
            {items.map((item, index) => (
              <MenuItem key={JSON.stringify(item.ref)} value={`library:${index}`}>
                <Choice thumb={item.thumb} label={item.label} />
              </MenuItem>
            ))}
          </Select>
        </div>
        {library && (
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
            {parts ? (
              parts.map((part) => (
                <React.Fragment key={part.variant}>
                  <input
                    ref={(el) => (partInputs.current[part.variant] = el)}
                    type="file"
                    hidden
                    accept={part.accept}
                    onChange={(event) => {
                      const file = event.target.files && event.target.files[0];
                      if (file) setStaged((current) => ({ ...current, [part.variant]: file }));
                      event.target.value = "";
                    }}
                  />
                  <Tooltip title={staged[part.variant] ? `${part.title}: ${staged[part.variant].name}` : part.title}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={isUploading}
                        onClick={() => (canEdit ? partInputs.current[part.variant] && partInputs.current[part.variant].click() : onReadOnly && onReadOnly())}
                        style={{ ...PART_BUTTON_STYLE, ...(staged[part.variant] ? STAGED_BUTTON_STYLE : null) }}
                      >
                        {staged[part.variant] ? `✓ ${part.button}` : `+ ${part.button}`}
                      </Button>
                    </span>
                  </Tooltip>
                </React.Fragment>
              ))
            ) : (
              <input ref={fileInput} type="file" hidden accept={library.accept} onChange={(event) => upload(event.target.files)} />
            )}
            <Tooltip
              title={
                parts && !partsReady
                  ? `Pick ${parts.map((part) => part.button).join(", ")} first`
                  : `Upload ${parts ? "them" : `a ${library.label}`} into ${bucket === NO_BUCKET ? "no bucket" : `the ${bucket} bucket`}`
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PublishIcon fontSize="small" />}
                  disabled={isUploading || (parts && !partsReady)}
                  onClick={() => {
                    if (!canEdit) return onReadOnly && onReadOnly();
                    if (def.kind === "model") return setOptimizerOpen(true);
                    if (parts) return upload(parts.map((part) => staged[part.variant]));
                    return fileInput.current && fileInput.current.click();
                  }}
                  style={OUTLINED_BUTTON_STYLE}
                >
                  Upload
                </Button>
              </span>
            </Tooltip>
          </div>
        )}
        {def.kind === "model" && (
          <DialogPe3dModelInspect
            open={inspecting === "model"}
            onClose={() => setInspecting(null)}
            title={def.label || slot}
            load={() => release.slotFileUrl(slot, value)}
            limits={limits}
            bytes={size}
          />
        )}
        {def.kind === "data" && (
          <DialogPe3dDataInspect
            open={inspecting === "data"}
            onClose={() => setInspecting(null)}
            title={def.label || slot}
            load={() => release.slotFileUrl(slot, value)}
          />
        )}

        {value && info && info.meta && (
          <Typography variant="caption" style={{ color: MUTED, display: "block", marginTop: 6 }}>
            {describeMeta(info.meta)}
          </Typography>
        )}
        {!library && (
          <Typography variant="caption" style={{ color: MUTED, display: "block", marginTop: 6 }}>
            A {def.kind} slot takes only files shipped with the game for now.
          </Typography>
        )}
        {errors.map((error) => (
          <Typography key={error} variant="caption" style={{ color: ERROR_TEXT, display: "block", marginTop: 4 }}>
            {error}
          </Typography>
        ))}
        {optimizerOpen && <Suspense fallback={<CircularProgress size={20} />}>
          <DialogGlbOptimizer open onClose={() => setOptimizerOpen(false)} allowZip={false}
            maxImportSizeMb={def.maxKB ? def.maxKB / 1024 : undefined}
            onImport={(file) => upload([file], true)} />
        </Suspense>}
      </CardContent>
    </Card>
  );
}
